// /home/coder/project/frontend/src/pages/machinery/machines/MachConstMachineDetail.tsx
import React from 'react';
import { Descriptions, Spin, Card, Row, Col, Typography, Result, message, Tabs, Table, Drawer, Form, Input, DatePicker, Select, InputNumber, Space, Tag } from 'antd';
import { ArrowLeftOutlined, ToolOutlined, AppstoreAddOutlined, HistoryOutlined, SafetyCertificateOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSqlData, insertData, updateData, deleteData } from '../../../services/api'; // Import shared fetch function
import type { VwAdmVehicleFuelEntryEnriched, VwConstMachines, Personnel } from '../../../types'; // Import necessary types
import { useGetPersonnel } from '../../../hooks/genericHooks';
import dayjs from 'dayjs';
import Button from '../../../components/common/Button';

// --- Related record types (mirror vehicle detail structure) ---
interface MachineMaintenance {
  id: number;
  vehicle_id: number;
  maintenance_date: string;
  description: string;
  created_by?: string;
  created_at?: string;
  maintenance_type_id?: number;
  performed_at_km?: number | null;
  performed_at_hours?: number | null;
}

interface MachineInspection {
  id: number;
  vehicle_id: number;
  inspection_date: string;
  description: string;
  created_by?: string;
  created_at?: string;
}

interface MachineTire {
  id: number;
  vehicle_id: number;
  tire_status: string;
  description: string;
  checked_at: string;
  created_by?: string;
  created_at?: string;
}

interface MachineRental {
  id: number;
  vehicle_id: number;
  rental_start: string;
  rental_end: string | null;
  contract_end: string | null;
  company_name: string;
  created_by?: string;
  created_at?: string;
}

interface MachineAssignment {
  id: number;
  vehicle_id: number;
  user_id: number;
  assigned_at: string;
  released_at: string | null;
  note?: string;
  created_by?: string;
  created_at?: string;
}

type VehicleFuelEntry = VwAdmVehicleFuelEntryEnriched;

// --- Helper functions (similar to AdmVehicleDetail) ---
const formatEnumString = (value: string | undefined | null): string => {
  if (!value) return '-';
  return String(value);
};

const formatDate = (dateString: string | undefined | null, locale = 'tr-TR'): string => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleString(locale, {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
  } catch (e) {
    return dateString; // Return original if parsing fails
  }
};

const MachConstMachineDetail: React.FC = () => {
  const locale = 'en-US';
  const translateEnum = (value?: string | null) => {
    if (!value) return '-';
    if (value === 'CompanyOwned' || value === 'company' || value === 'owned') return 'Company Owned';
    if (value === 'Rented' || value === 'rented' || value === 'rental') return 'Rented';
    if (value === 'Leased') return 'Leased';
    if (value === 'Available') return 'Available';
    if (value === 'Assigned') return 'Assigned';
    if (value === 'InMaintenance') return 'In Maintenance';
    if (value === 'OutOfService') return 'Out of Service';
    if (value === 'Inactive') return 'Inactive';
    return String(value);
  };
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const machineId = id ? parseInt(id, 10) : undefined; // Parse ID once
  const queryClient = useQueryClient();
  const [maintOpen, setMaintOpen] = React.useState(false);
  const [inspOpen, setInspOpen] = React.useState(false);
  const [tireOpen, setTireOpen] = React.useState(false);
  const [rentalOpen, setRentalOpen] = React.useState(false);
  const [assignOpen, setAssignOpen] = React.useState(false);
  const [recomputeLoading, setRecomputeLoading] = React.useState(false);
  const [fuelOpen, setFuelOpen] = React.useState(false);

  // --- Fetch Machine Details using the View ---
  const {
    data: machine,
    isLoading: isLoadingMachine,
    isError: isErrorMachine,
    error: errorMachine,
  } = useQuery<VwConstMachines, Error>({
    queryKey: ['machineDetail', machineId],
    queryFn: async () => {
        if (!machineId) throw new Error('No valid numeric ID was provided in the URL.');
        // Fetch from the view using the ID
        const results = await fetchSqlData('vw_const_machines', 'id', machineId);
        if (!results || !Array.isArray(results) || results.length === 0) {
            throw new Error(`No details found for machine ID ${machineId}`);
        }
        return results[0];
    },
    enabled: !!machineId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // --- Fetch related records using provided tables ---
  const { data: maintenances = [], isLoading: isLoadingMaintenances } = useQuery<MachineMaintenance[], Error>({
    queryKey: ['machineMaintenances', machineId],
    queryFn: () => fetchSqlData('adm_vehicle_maintenance', 'vehicle_id', machineId),
    enabled: !!machineId,
  });

  const { data: inspections = [], isLoading: isLoadingInspections } = useQuery<MachineInspection[], Error>({
    queryKey: ['machineInspections', machineId],
    queryFn: () => fetchSqlData('adm_vehicle_inspection', 'vehicle_id', machineId),
    enabled: !!machineId,
  });

  const { data: tires = [], isLoading: isLoadingTires } = useQuery<MachineTire[], Error>({
    queryKey: ['machineTires', machineId],
    queryFn: () => fetchSqlData('adm_vehicle_tires', 'vehicle_id', machineId),
    enabled: !!machineId,
  });

  const { data: rentals = [], isLoading: isLoadingRentals } = useQuery<MachineRental[], Error>({
    queryKey: ['machineRentals', machineId],
    queryFn: () => fetchSqlData('adm_vehicle_rental', 'vehicle_id', machineId),
    enabled: !!machineId,
  });

  const { data: assignments = [], isLoading: isLoadingAssignments } = useQuery<MachineAssignment[], Error>({
    queryKey: ['machineAssignments', machineId],
    queryFn: () => fetchSqlData('adm_vehicle_assignments', 'vehicle_id', machineId),
    enabled: !!machineId,
  });

  // --- Maintenance Alerts ---
  const { data: alerts = [], isLoading: isLoadingAlerts, refetch: refetchAlerts } = useQuery<any[], Error>({
    queryKey: ['maintenanceAlerts', machineId],
    queryFn: () => fetchSqlData('adm_maintenance_alerts', 'vehicle_id', machineId),
    enabled: !!machineId,
    staleTime: 1000 * 60,
  });

  // --- Fuel Entries ---
  const { data: fuelEntries = [], isLoading: isLoadingFuel, refetch: refetchFuel } = useQuery<VehicleFuelEntry[], Error>({
    queryKey: ['vehicleFuelEntries', machineId],
    queryFn: () => fetchSqlData('vw_adm_vehicle_fuel_entries_enriched', 'vehicle_id', machineId),
    enabled: !!machineId,
  });

  // --- Fetch Maintenance Types & Rules ---
  const { data: maintTypes = [] } = useQuery<any[], Error>({
    queryKey: ['adm_maintenance_types'],
    queryFn: () => fetchSqlData('adm_maintenance_types'),
    staleTime: 1000 * 60 * 10,
  });
  const { data: maintRules = [] } = useQuery<any[], Error>({
    queryKey: ['adm_maintenance_rules'],
    queryFn: () => fetchSqlData('adm_maintenance_rules'),
    staleTime: 1000 * 60 * 10,
  });

  const maintTypeMap = React.useMemo(() => {
    const m: Record<number, string> = {};
    (maintTypes || []).forEach((t: any) => { if (t.id) m[t.id] = t.name; });
    return m;
  }, [maintTypes]);

  // Filter maintenance types by machine type (if applicable)
  const maintTypeOptions = React.useMemo(() => {
    const mType = machine?.machine_type;
    const rows: any[] = Array.isArray(maintTypes) ? maintTypes : [];
    const parsePgArray = (val: any): string[] => {
      if (Array.isArray(val)) return val as string[];
      if (typeof val === 'string') {
        const trimmed = val.replace(/[{}]/g, '');
        return trimmed ? trimmed.split(',').map((s: string) => s.trim()) : [];
      }
      return [];
    };
    const filtered = mType
      ? rows.filter((t: any) => {
          const appl = parsePgArray(t.applicable_to_machine_types);
          // If no restriction set (empty), show for all; otherwise must include current machine type
          return appl.length === 0 || appl.includes(mType);
        })
      : rows;
    return filtered.map((t: any) => ({ value: t.id, label: t.name }));
  }, [maintTypes, machine]);

  // --- Fetch Lookup Data (Users/Personnel) for Display ---
  // Only needed if you display created_by/updated_by names and they aren't in the view
  const { data: personnelResp, isLoading: isLoadingUsers } = useGetPersonnel();
  const usersData = React.useMemo<Personnel[]>(() => {
    const arr = Array.isArray(personnelResp) ? personnelResp : (personnelResp as any)?.data || [];
    return arr as Personnel[];
  }, [personnelResp]);

  // --- Create Maps for Lookups ---
  const usersMap = React.useMemo(() => {
    const map: { [key: number]: string } = {};
    usersData.forEach(user => {
      if (user.id) map[user.id] = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User';
    });
    return map;
  }, [usersData]);

  // --- Loading and Error Handling ---
  const isOverallLoading = isLoadingMachine || isLoadingUsers; // Add other loading states if fetching related data

  if (!machineId) {
      return (
          <Result status="error" title={'Invalid Machine ID'} subTitle={'No valid numeric ID was provided in the URL.'}
              extra={<Button type="primary" onClick={() => navigate('/machinery/machines')}>{'Back'}</Button>} />
      );
  }

  if (isOverallLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isErrorMachine) {
    const errorMsg = (errorMachine as Error)?.message || 'Machine details could not be loaded.';
    message.error(errorMsg);
    return (
        <Result status="error" title={'Machine Could Not Be Loaded'} subTitle={errorMsg}
            extra={<Button type="primary" onClick={() => navigate('/machinery/machines')}>{'Back'}</Button>} />
    );
  }

  if (!machine) {
     return (
        <Result status="warning" title={'Machine Not Found'} subTitle={`No details found for machine ID ${machineId}`}
            extra={<Button type="primary" onClick={() => navigate('/machinery/machines')}>{'Back'}</Button>} />
    );
  }

  // --- Render Logic ---
  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 32 }}>
      <Row align="middle" gutter={16} style={{ marginBottom: 0 }}>
        <Col>
        <Button icon={<ArrowLeftOutlined />} onClick={() => {
              navigate(-1);
            }} size="large">{'Back'}</Button>
        </Col>
        <Col flex="auto">
          <Typography.Title level={2} style={{ margin: 0 }}>
            {`Machine Detail: ${machine.machine_type} (${machine.license_plate || 'No Plate'})`}
          </Typography.Title>
        </Col>
      </Row>
      <Card bordered={false}>
        <Tabs defaultActiveKey="detail" tabPosition="left">
          <Tabs.TabPane tab={<><ToolOutlined /> {'Machine Info'}</>} key="detail">
            <Descriptions bordered column={1} size="middle">
              <Descriptions.Item label={'ID'}>{machine.id}</Descriptions.Item>
              <Descriptions.Item label={'Type'}>{machine.machine_type || '-'}</Descriptions.Item>
              <Descriptions.Item label={'Plate / Serial No'}>{machine.license_plate || '-'}</Descriptions.Item>
              <Descriptions.Item label={'Make'}>{machine.make || '-'}</Descriptions.Item>
              <Descriptions.Item label={'Model'}>{machine.model || '-'}</Descriptions.Item>
              <Descriptions.Item label={'Year'}>{machine.year || '-'}</Descriptions.Item>
              <Descriptions.Item label={'Kilometer'}>{((machine as any).kilometer ?? 0).toLocaleString(locale)}</Descriptions.Item>
              <Descriptions.Item label={'Hours'}>{machine.hours_counter?.toLocaleString(locale) || '0'}</Descriptions.Item>
              <Descriptions.Item label={'Arrival Date'}>
                {machine.arrival_date ? dayjs(machine.arrival_date).format('DD/MM/YYYY') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={'Departure Date'}>
                {machine.departure_date ? dayjs(machine.departure_date).format('DD/MM/YYYY') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={'Ownership'}>{translateEnum(machine.ownership_type)}</Descriptions.Item>
              <Descriptions.Item label={'Contract'}>{machine.contract_reference_id || '-'}</Descriptions.Item>
              <Descriptions.Item label={'Company'}>{machine.company_name || '-'}</Descriptions.Item>
              <Descriptions.Item label={'Operator'}>{machine.operator_name || '-'}</Descriptions.Item>
              <Descriptions.Item label={'Status'}>{translateEnum(machine.status)}</Descriptions.Item>
              <Descriptions.Item label={'Specifications'}>
                {(() => {
                  const specs = (machine as any).specs as Record<string, any> | null | undefined;
                  if (!specs || typeof specs !== 'object') return '-';
                  const entries = Object.entries(specs).filter(
                    ([k, v]) => k && v != null && String(v).trim() !== ''
                  );
                  if (!entries.length) return '-';
                  return entries.map(([k, v]) => `${k}: ${String(v)}`).join(' | ');
                })()}
              </Descriptions.Item>
              {/* Add created_at/updated_at if available in the view and needed */}
              {/* <Descriptions.Item label="Created At">{formatDate(machine.created_at)}</Descriptions.Item> */}
            </Descriptions>
          </Tabs.TabPane>
          <Tabs.TabPane tab={<><ToolOutlined /> {'Maintenance Records'}</>} key="maintenance">
            <Row justify="end" style={{ marginBottom: 12 }}>
              <Col>
                <Button action="add" type="primary" icon={<PlusOutlined />} onClick={() => setMaintOpen(true)}>{'New Maintenance'}</Button>
              </Col>
            </Row>
            <Table<MachineMaintenance>
              loading={isLoadingMaintenances}
              dataSource={maintenances}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="middle"
              columns={[
                { title: 'Type', dataIndex: 'maintenance_type_id', key: 'maintenance_type_id', render: (id) => maintTypeMap[id] || '-' },
                { title: 'Maintenance Date', dataIndex: 'maintenance_date', key: 'maintenance_date', render: (d) => formatDate(d, locale) },
                { title: 'Description', dataIndex: 'description', key: 'description' },
                { title: 'Performed KM', dataIndex: 'performed_at_km', key: 'performed_at_km', render: (v) => v ?? '-' },
                { title: 'Performed Hours', dataIndex: 'performed_at_hours', key: 'performed_at_hours', render: (v) => v ?? '-' },
                // { title: 'Oluşturan', dataIndex: 'created_by', key: 'created_by' },
              ]}
              locale={{ emptyText: 'No records found' }}
              style={{ background: '#fff', borderRadius: 8 }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab={<><ToolOutlined /> {'Fuel Records'}</>} key="fuel">
            <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
              <Col>
                <Typography.Text>{'Fuel entries'}</Typography.Text>
              </Col>
              <Col>
                <Button action="add" type="primary" icon={<PlusOutlined />} onClick={() => setFuelOpen(true)}>{'New Fuel'}</Button>
              </Col>
            </Row>
            <Table<VehicleFuelEntry>
              dataSource={fuelEntries}
              loading={isLoadingFuel}
              rowKey="id"
              size="middle"
              pagination={{ pageSize: 10 }}
              columns={[
                { title: 'Date', dataIndex: 'entry_date', key: 'entry_date', render: (d) => formatDate(d, locale) },
                { title: 'Fuel (L)', dataIndex: 'liters', key: 'liters', render: (v) => v?.toLocaleString(locale, { maximumFractionDigits: 2 }) },
                {
                  title: 'Source Type',
                  dataIndex: 'source_type',
                  key: 'source_type',
                  render: (v) => v === 'fixed_pump'
                    ? 'Fixed Pump'
                    : v === 'fuel_tanker'
                      ? 'Fuel Tanker'
                      : v === 'external'
                        ? 'External Source'
                        : '-',
                },
                {
                  title: 'Source',
                  dataIndex: 'source_name',
                  key: 'source_name',
                  render: (_value, row) => row.source_pump_name || row.source_name || row.source_vehicle_license_plate || row.fuel_station || '-',
                },
                { title: 'KM', dataIndex: 'odometer_reading', key: 'odometer_reading', render: (v) => v ?? '-' },
                { title: 'Hours', dataIndex: 'hours_counter_reading', key: 'hours_counter_reading', render: (v) => v ?? '-' },
                { title: 'Company', dataIndex: 'company', key: 'company', render: (v) => v || '-' },
                { title: 'Note', dataIndex: 'note', key: 'note', render: (v) => v || '-' },
              ]}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab={<><SafetyCertificateOutlined /> {'Inspection Records'}</>} key="inspection">
            <Row justify="end" style={{ marginBottom: 12 }}>
              <Col>
                <Button action="add" type="primary" icon={<PlusOutlined />} onClick={() => setInspOpen(true)}>{'New Inspection'}</Button>
              </Col>
            </Row>
            <Table<MachineInspection>
              loading={isLoadingInspections}
              dataSource={inspections}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="middle"
              columns={[
                { title: 'Inspection Date', dataIndex: 'inspection_date', key: 'inspection_date', render: (d) => formatDate(d, locale) },
                { title: 'Description', dataIndex: 'description', key: 'description' },
              ]}
              locale={{ emptyText: 'No records found' }}
              style={{ background: '#fff', borderRadius: 8 }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab={<><AppstoreAddOutlined /> {'Tire Records'}</>} key="tire">
            <Row justify="end" style={{ marginBottom: 12 }}>
              <Col>
                <Button action="add" type="primary" icon={<PlusOutlined />} onClick={() => setTireOpen(true)}>{'New Tire Record'}</Button>
              </Col>
            </Row>
            <Table<MachineTire>
              loading={isLoadingTires}
              dataSource={tires}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="middle"
              columns={[
                { title: 'Status', dataIndex: 'tire_status', key: 'tire_status' },
                { title: 'Description', dataIndex: 'description', key: 'description' },
                { title: 'Checked At', dataIndex: 'checked_at', key: 'checked_at', render: (d) => formatDate(d, locale) },
              ]}
              locale={{ emptyText: 'No records found' }}
              style={{ background: '#fff', borderRadius: 8 }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab={<><ToolOutlined /> {'Rental Records'}</>} key="rental">
            <Row justify="end" style={{ marginBottom: 12 }}>
              <Col>
                <Button action="add" type="primary" icon={<PlusOutlined />} onClick={() => setRentalOpen(true)}>{'New Rental'}</Button>
              </Col>
            </Row>
            <Table<MachineRental>
              loading={isLoadingRentals}
              dataSource={rentals}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="middle"
              columns={[
                { title: 'Start', dataIndex: 'rental_start', key: 'rental_start', render: (d) => formatDate(d, locale) },
                { title: 'End', dataIndex: 'rental_end', key: 'rental_end', render: (d) => formatDate(d, locale) },
                { title: 'Contract End', dataIndex: 'contract_end', key: 'contract_end', render: (d) => formatDate(d, locale) },
                { title: 'Company', dataIndex: 'company_name', key: 'company_name' },
              ]}
              locale={{ emptyText: 'No records found' }}
              style={{ background: '#fff', borderRadius: 8 }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab={<><HistoryOutlined /> {'Assignment History'}</>} key="assignment">
            <Row justify="end" style={{ marginBottom: 12 }}>
              <Col>
                <Button action="add" type="primary" icon={<PlusOutlined />} onClick={() => setAssignOpen(true)}>{'New Assignment'}</Button>
              </Col>
            </Row>
            <Table<MachineAssignment>
              loading={isLoadingAssignments}
              dataSource={assignments}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="middle"
              columns={[
                { title: 'User', dataIndex: 'user_id', key: 'user_id', render: (userId: number) => usersMap[userId] || '-' },
                { title: 'Start', dataIndex: 'assigned_at', key: 'assigned_at', render: (d) => formatDate(d, locale) },
                { title: 'End', dataIndex: 'released_at', key: 'released_at', render: (d) => formatDate(d, locale) },
                { title: 'Note', dataIndex: 'note', key: 'note' },
              ]}
              locale={{ emptyText: 'No records found' }}
              style={{ background: '#fff', borderRadius: 8 }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab={<><HistoryOutlined /> {'Maintenance Alerts'}</>} key="alerts">
            <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
              <Col>
                <Typography.Text>{'Active maintenance alerts for this machine'}</Typography.Text>
              </Col>
              <Col>
                <Button type="primary" loading={recomputeLoading} onClick={async () => {
                  if (!machine) return;
                  setRecomputeLoading(true);
                  try {
                    // Build maps for quick lookup
                    const rulesByType: Record<number, any[]> = {};
                    (maintRules || []).forEach((r: any) => {
                      const key = Number(r.maintenance_type_id);
                      if (!rulesByType[key]) rulesByType[key] = [];
                      rulesByType[key].push(r);
                    });
                    const lastByType: Record<number, MachineMaintenance | undefined> = {};
                    (maintenances || []).forEach((m) => {
                      const t = (m as any).maintenance_type_id as number | undefined;
                      if (!t) return;
                      const prev = lastByType[t];
                      if (!prev) lastByType[t] = m;
                      else {
                        const prevDate = new Date(prev.maintenance_date || 0).getTime();
                        const curDate = new Date(m.maintenance_date || 0).getTime();
                        if (curDate > prevDate) lastByType[t] = m;
                      }
                    });

                    const existingAlerts: any[] = Array.isArray(alerts) ? alerts : [];

                    const createdAtRaw = (machine as any).created_at as string | undefined;
                    const createdAt = createdAtRaw ? new Date(createdAtRaw) : new Date();
                    const currentKm = Number((machine as any).kilometer || 0) || 0;
                    const currentHours = Number((machine as any).hours_counter || 0) || 0;
                    const now = new Date();

                    // helper date add days
                    const addDays = (d: Date, days?: number | null) => {
                      if (!days || isNaN(days)) return null;
                      const nd = new Date(d);
                      nd.setDate(nd.getDate() + Number(days));
                      return nd;
                    };

                    // Iterate over applicable maintenance types
                    const typeRows: any[] = Array.isArray(maintTypes) ? maintTypes : [];
                    // Filter by machine type applicability
                    const parsePgArray = (val: any): string[] => {
                      if (Array.isArray(val)) return val as string[];
                      if (typeof val === 'string') {
                        const trimmed = val.replace(/[{}]/g, '');
                        return trimmed ? trimmed.split(',').map((s: string) => s.trim()) : [];
                      }
                      return [];
                    };
                    const applicableTypes = typeRows.filter((t: any) => {
                      const appl = parsePgArray(t.applicable_to_machine_types);
                      return appl.length === 0 || appl.includes(machine.machine_type);
                    });

                    for (const t of applicableTypes) {
                      const typeId = Number(t.id);
                      const rules = rulesByType[typeId] || [];
                      if (rules.length === 0) continue; // no rule, no alert

                      const last = lastByType[typeId];
                      const lastKm = last?.performed_at_km ?? 0;
                      const lastHours = last?.performed_at_hours ?? 0;
                      const lastDate = last?.maintenance_date ? new Date(last.maintenance_date) : createdAt;

                      // Aggregate rules (use max thresholds, min intervals?)
                      // Here, evaluate per dimension independently and pick the earliest due among provided intervals.
                      let targetKm: number | null = null;
                      let warnKm: number | null = null;
                      let targetHours: number | null = null;
                      let warnHours: number | null = null;
                      let targetDate: Date | null = null;
                      let warnDate: Date | null = null;

                      rules.forEach((r: any) => {
                        if (r.interval_km != null) {
                          const tk = Number(lastKm) + Number(r.interval_km);
                          targetKm = targetKm == null ? tk : Math.min(targetKm, tk);
                          if (r.warning_threshold_km != null) {
                            const wk = tk - Number(r.warning_threshold_km);
                            warnKm = warnKm == null ? wk : Math.min(warnKm, wk);
                          }
                        }
                        if (r.interval_hours != null) {
                          const th = Number(lastHours) + Number(r.interval_hours);
                          targetHours = targetHours == null ? th : Math.min(targetHours, th);
                          if (r.warning_threshold_hours != null) {
                            const wh = th - Number(r.warning_threshold_hours);
                            warnHours = warnHours == null ? wh : Math.min(warnHours, wh);
                          }
                        }
                        if (r.interval_days != null) {
                          const td = addDays(lastDate, Number(r.interval_days));
                          if (td) targetDate = !targetDate || td < targetDate ? td : targetDate;
                          if (r.warning_threshold_days != null) {
                            const wd = addDays(td!, -Number(r.warning_threshold_days));
                            if (wd) warnDate = !warnDate || wd < warnDate ? wd : warnDate;
                          }
                        }
                      });

                      // Evaluate status
                      let status: string | null = null;
                      const dueChecks: Array<'Due'|'Approaching'|null> = [];
                      if (targetKm != null) {
                        if (currentKm >= targetKm) dueChecks.push('Due');
                        else if (warnKm != null && currentKm >= warnKm) dueChecks.push('Approaching');
                        else dueChecks.push(null);
                      }
                      if (targetHours != null) {
                        if (currentHours >= targetHours) dueChecks.push('Due');
                        else if (warnHours != null && currentHours >= warnHours) dueChecks.push('Approaching');
                        else dueChecks.push(null);
                      }
                      if (targetDate != null) {
                        if (now >= (targetDate as Date)) dueChecks.push('Due');
                        else if (warnDate != null && now >= (warnDate as Date)) dueChecks.push('Approaching');
                        else dueChecks.push(null);
                      }
                      if (dueChecks.includes('Due')) status = 'Due';
                      else if (dueChecks.includes('Approaching')) status = 'Approaching';
                      else status = null;

                      const existing = existingAlerts.find(a => Number(a.maintenance_type_id) === typeId);
                      if (!status) {
                        if (existing) {
                          await deleteData('adm_maintenance_alerts', 'id', existing.id);
                        }
                        continue;
                      }
                      const payload: any = {
                        vehicle_id: machineId,
                        maintenance_type_id: typeId,
                        alert_status: status,
                        due_km: targetKm ?? null,
                        due_hours: targetHours ?? null,
                        due_date: targetDate ? (targetDate as Date).toISOString().slice(0,10) : null,
                      };
                      if (existing) await updateData('adm_maintenance_alerts', Number(existing.id), payload);
                      else await insertData('adm_maintenance_alerts', payload);
                    }

                    await refetchAlerts();
                    message.success('Alerts recalculated');
                  } catch (e: any) {
                    console.error(e);
                    message.error('An error occurred while calculating alerts');
                  } finally { setRecomputeLoading(false); }
                }}>{'Recalculate Alerts'}</Button>
              </Col>
            </Row>
            <Table
              dataSource={alerts}
              rowKey="id"
              loading={isLoadingAlerts}
              size="middle"
              pagination={{ pageSize: 10 }}
              columns={[
                { title: 'Maintenance Type', dataIndex: 'maintenance_type_id', key: 'maintenance_type_id', render: (id: number) => maintTypeMap[id] || '-' },
                { title: 'Status', dataIndex: 'alert_status', key: 'alert_status', render: (s: string) => {
                  let color: any = 'default';
                  if (s === 'Approaching') color = 'warning';
                  if (s === 'Due') color = 'error';
                  if (s === 'Overdue') color = 'magenta';
                  const label = s === 'Approaching' ? 'Approaching' : s === 'Due' ? 'Due' : s === 'Overdue' ? 'Overdue' : s;
                  return <Tag color={color}>{label}</Tag>;
                } },
                { title: 'Due Date', dataIndex: 'due_date', key: 'due_date', render: (d: any) => d ? formatDate(d, locale) : '-' },
                { title: 'Due KM', dataIndex: 'due_km', key: 'due_km', render: (v: any) => v ?? '-' },
                { title: 'Due Hours', dataIndex: 'due_hours', key: 'due_hours', render: (v: any) => v ?? '-' },
                { title: 'Note', dataIndex: 'notes', key: 'notes' },
              ]}
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>

      {/* Drawers for creating records */}
      <CreateMaintenanceDrawer
        open={maintOpen}
        onClose={() => setMaintOpen(false)}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['machineMaintenances', machineId] })}
        machineId={machineId!}
        types={maintTypeOptions}
        typesLoading={!maintTypes}
      />
      <CreateInspectionDrawer open={inspOpen} onClose={() => setInspOpen(false)} onSuccess={() => queryClient.invalidateQueries({ queryKey: ['machineInspections', machineId] })} machineId={machineId!} />
      <CreateTireDrawer open={tireOpen} onClose={() => setTireOpen(false)} onSuccess={() => queryClient.invalidateQueries({ queryKey: ['machineTires', machineId] })} machineId={machineId!} />
      <CreateRentalDrawer open={rentalOpen} onClose={() => setRentalOpen(false)} onSuccess={() => queryClient.invalidateQueries({ queryKey: ['machineRentals', machineId] })} machineId={machineId!} />
      <CreateAssignmentDrawer open={assignOpen} onClose={() => setAssignOpen(false)} onSuccess={() => queryClient.invalidateQueries({ queryKey: ['machineAssignments', machineId] })} machineId={machineId!} users={usersData} />
      <CreateFuelEntryDrawer open={fuelOpen} onClose={() => setFuelOpen(false)} onSuccess={() => refetchFuel()} machineId={machineId!} />
    </div>
  );
};

export default MachConstMachineDetail;

// --- Create Drawers ---
function CreateMaintenanceDrawer({ open, onClose, onSuccess, machineId, types, typesLoading }: { open: boolean; onClose: () => void; onSuccess: () => void; machineId: number; types: { value: number; label: string }[]; typesLoading?: boolean; }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await insertData('adm_vehicle_maintenance', {
        vehicle_id: machineId,
        maintenance_date: values.maintenance_date?.toISOString(),
        description: values.description?.trim() || null,
        maintenance_type_id: values.maintenance_type_id || null,
        performed_at_km: values.performed_at_km ?? null,
        performed_at_hours: values.performed_at_hours ?? null,
      });
      onSuccess();
      onClose();
    } finally { setLoading(false); }
  };
  return (
    <Drawer title={'New Maintenance'} open={open} onClose={onClose} width={520} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="maintenance_type_id" label={'Maintenance Type'}>
          <Select options={types} allowClear showSearch loading={typesLoading} placeholder={typesLoading ? 'Loading...' : 'Select maintenance type'} />
        </Form.Item>
        <Form.Item name="maintenance_date" label={'Maintenance Date'} rules={[{ required: true, message: 'Date is required' }]}>
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="performed_at_km" label={'Performed KM'}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="performed_at_hours" label={'Performed Hours'}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="description" label={'Description'}>
          <Input.TextArea rows={4} />
        </Form.Item>
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>{'Cancel'}</Button>
          <Button action="add" type="primary" onClick={handleSubmit} loading={loading}>{'Save'}</Button>
        </Space>
      </Form>
    </Drawer>
  );
}

function CreateInspectionDrawer({ open, onClose, onSuccess, machineId }: { open: boolean; onClose: () => void; onSuccess: () => void; machineId: number; }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await insertData('adm_vehicle_inspection', {
        vehicle_id: machineId,
        inspection_date: values.inspection_date?.toISOString(),
        description: values.description?.trim() || null,
      });
      onSuccess();
      onClose();
    } finally { setLoading(false); }
  };
  return (
    <Drawer title={'New Inspection'} open={open} onClose={onClose} width={520} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="inspection_date" label={'Inspection Date'} rules={[{ required: true, message: 'Date is required' }]}>
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="description" label={'Description'}>
          <Input.TextArea rows={4} />
        </Form.Item>
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>{'Cancel'}</Button>
          <Button action="add" type="primary" onClick={handleSubmit} loading={loading}>{'Save'}</Button>
        </Space>
      </Form>
    </Drawer>
  );
}

function CreateTireDrawer({ open, onClose, onSuccess, machineId }: { open: boolean; onClose: () => void; onSuccess: () => void; machineId: number; }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await insertData('adm_vehicle_tires', {
        vehicle_id: machineId,
        tire_status: values.tire_status,
        description: values.description?.trim() || null,
        checked_at: values.checked_at?.toISOString(),
      });
      onSuccess();
      onClose();
    } finally { setLoading(false); }
  };
  return (
    <Drawer title={'New Tire Record'} open={open} onClose={onClose} width={520} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="tire_status" label={'Status'} rules={[{ required: true, message: 'Status is required' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label={'Description'}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name="checked_at" label={'Checked At'} rules={[{ required: true, message: 'Date is required' }]}>
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>{'Cancel'}</Button>
          <Button action="add" type="primary" onClick={handleSubmit} loading={loading}>{'Save'}</Button>
        </Space>
      </Form>
    </Drawer>
  );
}

function CreateRentalDrawer({ open, onClose, onSuccess, machineId }: { open: boolean; onClose: () => void; onSuccess: () => void; machineId: number; }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await insertData('adm_vehicle_rental', {
        vehicle_id: machineId,
        rental_start: values.rental_start?.toISOString(),
        rental_end: values.rental_end ? values.rental_end.toISOString() : null,
        contract_end: values.contract_end ? values.contract_end.toISOString() : null,
        company_name: values.company_name?.trim() || null,
      });
      onSuccess();
      onClose();
    } finally { setLoading(false); }
  };
  return (
    <Drawer title={'New Rental'} open={open} onClose={onClose} width={520} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="company_name" label={'Company'}>
          <Input />
        </Form.Item>
        <Form.Item name="rental_start" label={'Start'} rules={[{ required: true, message: 'Start is required' }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="rental_end" label={'End'}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="contract_end" label={'Contract End'}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>{'Cancel'}</Button>
          <Button action="add" type="primary" onClick={handleSubmit} loading={loading}>{'Save'}</Button>
        </Space>
      </Form>
    </Drawer>
  );
}

function CreateAssignmentDrawer({ open, onClose, onSuccess, machineId, users }: { open: boolean; onClose: () => void; onSuccess: () => void; machineId: number; users: Personnel[]; }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await insertData('adm_vehicle_assignments', {
        vehicle_id: machineId,
        user_id: values.user_id,
        assigned_at: values.assigned_at?.toISOString(),
        released_at: values.released_at ? values.released_at.toISOString() : null,
        note: values.note?.trim() || null,
      });
      onSuccess();
      onClose();
    } finally { setLoading(false); }
  };
  return (
    <Drawer title={'New Assignment'} open={open} onClose={onClose} width={520} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="user_id" label={'User'} rules={[{ required: true, message: 'User is required' }]}>
          <Select showSearch options={(users || []).map(u => ({ value: u.id, label: `${u.first_name || ''} ${u.last_name || ''}`.trim() }))} />
        </Form.Item>
        <Form.Item name="assigned_at" label={'Start'} rules={[{ required: true, message: 'Start is required' }]}>
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="released_at" label={'End'}>
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="note" label={'Note'}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>{'Cancel'}</Button>
          <Button action="add" type="primary" onClick={handleSubmit} loading={loading}>{'Save'}</Button>
        </Space>
      </Form>
    </Drawer>
  );
}

function CreateFuelEntryDrawer({ open, onClose, onSuccess, machineId }: { open: boolean; onClose: () => void; onSuccess: () => void; machineId: number; }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await insertData('adm_vehicle_fuel_entries', {
        vehicle_id: machineId,
        entry_date: values.entry_date?.toISOString(),
        liters: Number(values.liters),
        odometer_reading: values.odometer_reading ?? null,
        hours_counter_reading: values.hours_counter_reading ?? null,
        source_type: 'external',
        source_name: values.fuel_station?.trim() || null,
        fuel_station: values.fuel_station?.trim() || null,
        company: values.company?.trim() || null,
        note: values.note?.trim() || null,
      });
      onSuccess();
      onClose();
    } finally { setLoading(false); }
  };
  return (
    <Drawer title={'New Fuel Entry'} open={open} onClose={onClose} width={520} destroyOnClose>
      <Form form={form} layout="vertical" initialValues={{ liters: undefined }}>
        <Form.Item name="entry_date" label={'Date'} rules={[{ required: true, message: 'Date is required' }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="liters" label={'Fuel (L)'} rules={[{ required: true, message: 'Required' }]}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="odometer_reading" label={'KM'}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="hours_counter_reading" label={'Hours'}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="fuel_station" label={'Station'}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="company" label={'Company'}>
          <Input />
        </Form.Item>
        <Form.Item name="note" label={'Note'}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>{'Cancel'}</Button>
          <Button action="add" type="primary" onClick={handleSubmit} loading={loading}>{'Save'}</Button>
        </Space>
      </Form>
    </Drawer>
  );
}
